import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Users, Scale, Download, CheckCircle, Plus, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';

export function ParticipantsTab({ isAdmin }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPesee, setShowPesee] = useState(null);
  const [peseeForm, setPeseeForm] = useState({ poids: '', mg: '', mm: '' });
  const [editJ0, setEditJ0] = useState(null);
  const [j0Form, setJ0Form] = useState({ poids_j0: '', mg_j0: '', mm_j0: '', objectif: 'gras', pct_objectif: '7' });
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showMetrics, setShowMetrics] = useState(null);
  const [metricsData, setMetricsData] = useState(null);
  const [metricsForm, setMetricsForm] = useState({ weight: '', body_fat: '', muscle_mass: '', bone_mass: '', body_water: '', visceral_fat: '', bmr: '', metabolic_age: '' });
  const [savingMetrics, setSavingMetrics] = useState(false);

  const fetchList = useCallback(() => {
    apiClient.get('/challenge/participants').then(r => setParticipants(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const loadMeute = async () => {
    try { const { data } = await apiClient.post('/challenge/load-meute'); toast.success(`${data.added} participants ajoutes`); fetchList(); } catch { toast.error('Erreur'); }
  };
  const syncMembers = async () => {
    try { const { data } = await apiClient.post('/challenge/sync-membres'); toast.success(`${data.added} nouveaux membres synchronises (total: ${data.total})`); fetchList(); } catch { toast.error('Erreur'); }
  };
  const toggleActif = async (p) => {
    try { await apiClient.put(`/challenge/toggle-actif/${p.id}`); fetchList(); } catch { toast.error('Erreur'); }
  };
  const toggleCaution = async (p) => {
    try {
      await apiClient.put(`/challenge/toggle-caution/${p.id}`);
      toast.success(p.caution_p1_ok ? `Caution retiree pour ${p.nom}` : `Caution validee pour ${p.nom}`);
      fetchList();
    } catch { toast.error('Erreur'); }
  };
  const savePesee = async () => {
    if (!showPesee || !peseeForm.poids) return;
    try {
      await apiClient.post(`/challenge/mesures/${showPesee}`, { poids: parseFloat(peseeForm.poids), mg: parseFloat(peseeForm.mg), mm: parseFloat(peseeForm.mm) });
      toast.success('Pesee enregistree'); setShowPesee(null); setPeseeForm({ poids: '', mg: '', mm: '' });
    } catch { toast.error('Erreur'); }
  };
  const saveJ0 = async () => {
    if (!editJ0) return;
    try {
      await apiClient.put(`/challenge/participants/${editJ0}`, { poids_j0: parseFloat(j0Form.poids_j0) || null, mg_j0: parseFloat(j0Form.mg_j0) || null, mm_j0: parseFloat(j0Form.mm_j0) || null, objectif: j0Form.objectif, pct_objectif: parseFloat(j0Form.pct_objectif) || 7 });
      toast.success('J0 mis a jour'); setEditJ0(null); fetchList();
    } catch { toast.error('Erreur'); }
  };

  const handleCreateMember = async () => {
    if (!createName.trim()) { toast.error('Entre un nom'); return; }
    setCreating(true);
    try {
      const { data } = await apiClient.post('/challenge/create-member', { nom: createName.trim() });
      toast.success(`${data.nom} ${data.created_user ? 'cree et ajoute' : 'ajoute'} au challenge !`);
      setCreateName(''); setShowCreate(false); fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const openMetrics = async (p) => {
    setShowMetrics(p.id);
    setMetricsData(null);
    setMetricsForm({ weight: '', body_fat: '', muscle_mass: '', bone_mass: '', body_water: '', visceral_fat: '', bmr: '', metabolic_age: '' });
    try {
      const { data } = await apiClient.get(`/challenge/participants/${p.id}/metrics`);
      setMetricsData(data);
      if (data.metrics) {
        setMetricsForm({
          weight: data.metrics.weight || '',
          body_fat: data.metrics.body_fat || '',
          muscle_mass: data.metrics.muscle_mass || '',
          bone_mass: data.metrics.bone_mass || '',
          body_water: data.metrics.body_water || '',
          visceral_fat: data.metrics.visceral_fat || '',
          bmr: data.metrics.bmr || '',
          metabolic_age: data.metrics.metabolic_age || '',
        });
      }
    } catch { toast.error('Pas de profil app lie'); setShowMetrics(null); }
  };

  const saveMetrics = async () => {
    if (!showMetrics) return;
    setSavingMetrics(true);
    try {
      const payload = {};
      if (metricsForm.weight) payload.weight = parseFloat(metricsForm.weight);
      if (metricsForm.body_fat) payload.body_fat = parseFloat(metricsForm.body_fat);
      if (metricsForm.muscle_mass) payload.muscle_mass = parseFloat(metricsForm.muscle_mass);
      if (metricsForm.bone_mass) payload.bone_mass = parseFloat(metricsForm.bone_mass);
      if (metricsForm.body_water) payload.body_water = parseFloat(metricsForm.body_water);
      if (metricsForm.visceral_fat) payload.visceral_fat = parseInt(metricsForm.visceral_fat);
      if (metricsForm.bmr) payload.bmr = parseFloat(metricsForm.bmr);
      if (metricsForm.metabolic_age) payload.metabolic_age = parseInt(metricsForm.metabolic_age);
      await apiClient.put(`/challenge/participants/${showMetrics}/metrics`, payload);
      toast.success('Metriques enregistrees !'); setShowMetrics(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingMetrics(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 pb-24 space-y-3">
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-white hover:bg-[#E85A25]" size="sm" data-testid="create-member-btn">
            <Plus className="w-4 h-4 mr-1" /> Creer membre
          </Button>
        </div>
      )}

      <p className="text-xs text-[#AAAAAA]">Coche les membres a activer dans le challenge :</p>

      {participants.map(p => (
        <div key={p.id} className={`bg-[#1B1B1B] border rounded-lg p-3 ${p.actif ? 'border-[#2e7d32]/40' : 'border-[#2A2A2E]'}`}>
          <div className="flex items-center gap-2">
            {isAdmin ? (
              <button onClick={() => toggleActif(p)} data-testid={`toggle-actif-${p.id}`}
                className={`w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center transition-colors cursor-pointer active:scale-95 ${p.actif ? 'bg-[#2e7d32] text-white' : 'border-2 border-zinc-600 hover:border-[#FF6B35]'}`}>
                {p.actif && <CheckCircle className="w-5 h-5" />}
              </button>
            ) : (
              <div className={`w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center ${p.actif ? 'bg-[#2e7d32] text-white' : 'border-2 border-zinc-600'}`}>
                {p.actif && <CheckCircle className="w-4 h-4" />}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className={`text-sm font-medium ${p.actif ? 'text-white' : 'text-zinc-500'}`}>{p.nom}</span>
              {p.caution_p1_ok && <span className="ml-1.5 text-[9px] text-[#FFB800] bg-[#FFB800]/15 px-1.5 py-0.5 rounded font-bold">Caution</span>}
              {!p.caution_p1_ok && p.actif && <span className="ml-1.5 text-[9px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">Sans caution</span>}
            </div>
            <span className="text-[10px] text-[#AAAAAA] hidden sm:inline">{p.poids_j0 ? `${p.poids_j0}kg · -${p.pct_objectif || 7}%` : 'Pas de J0'}</span>
            {isAdmin && (
              <div className="flex gap-1">
                <button onClick={() => toggleCaution(p)}
                  className={`px-2 py-1 text-[10px] rounded-sm font-bold ${p.caution_p1_ok ? 'bg-[#FFB800]/20 text-[#FFB800]' : 'bg-zinc-800 text-zinc-500 hover:text-[#FFB800]'}`}
                  data-testid={`caution-btn-${p.id}`}>
                  {p.caution_p1_ok ? '€✓' : '€'}
                </button>
                <button onClick={() => { setEditJ0(p.id); setJ0Form({ poids_j0: p.poids_j0 || '', mg_j0: p.mg_j0 || '', mm_j0: p.mm_j0 || '', objectif: p.objectif || 'gras', pct_objectif: p.pct_objectif || '7' }); }}
                  className="px-2 py-1 text-[10px] bg-[#A1A1AA]/10 text-[#A1A1AA] rounded-sm" data-testid={`j0-btn-${p.id}`}>J0</button>
                <button onClick={() => openMetrics(p)}
                  className="px-2 py-1 text-[10px] bg-[#FF00B8]/15 text-[#FF00B8] rounded-sm" data-testid={`metrics-btn-${p.id}`}>
                  <Activity className="w-3 h-3" />
                </button>
                <button onClick={() => setShowPesee(p.id)}
                  className="px-2 py-1 text-[10px] bg-[#FF6B35]/20 text-[#FF6B35] rounded-sm" data-testid={`pesee-btn-${p.id}`}>
                  <Scale className="w-3 h-3" />
                </button>
                <a href={`${window.location.origin}/api/challenge/inscription-pdf/${p.id}`} target="_blank" rel="noreferrer"
                  className="px-2 py-1 text-[10px] bg-[#2e7d32]/20 text-[#2e7d32] rounded-sm">
                  <Download className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Create Member Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Creer un nouveau membre</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#AAAAAA]">Cree un compte dans l'app ET l'ajoute comme participant du challenge.</p>
            <div>
              <Label className="text-[#AAAAAA] text-xs">Prenom / Pseudo</Label>
              <Input value={createName} onChange={e => setCreateName(e.target.value)} placeholder="Ex: Hiro"
                className="bg-[#1B1B1B] border-[#2A2A2E] text-white" data-testid="create-member-name" />
            </div>
            <Button onClick={handleCreateMember} disabled={creating || !createName.trim()}
              className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55] disabled:opacity-40" data-testid="submit-create-member">
              {creating ? 'Creation...' : 'Creer le membre'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pesee Dialog */}
      <Dialog open={!!showPesee} onOpenChange={() => setShowPesee(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#A1A1AA]/20">
          <DialogHeader><DialogTitle className="text-[#FAFAFA]">Saisie pesee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[#A1A1AA] text-xs">Poids (kg)</Label><Input type="number" step="0.1" value={peseeForm.poids} onChange={e => setPeseeForm({...peseeForm, poids: e.target.value})} className="bg-[#1B1B1B] border-[#A1A1AA]/20 text-[#FAFAFA] font-mono" /></div>
            <div><Label className="text-[#A1A1AA] text-xs">Masse graisseuse (%)</Label><Input type="number" step="0.1" value={peseeForm.mg} onChange={e => setPeseeForm({...peseeForm, mg: e.target.value})} className="bg-[#1B1B1B] border-[#A1A1AA]/20 text-[#FAFAFA] font-mono" /></div>
            <div><Label className="text-[#A1A1AA] text-xs">Masse musculaire (kg)</Label><Input type="number" step="0.1" value={peseeForm.mm} onChange={e => setPeseeForm({...peseeForm, mm: e.target.value})} className="bg-[#1B1B1B] border-[#A1A1AA]/20 text-[#FAFAFA] font-mono" /></div>
            <Button onClick={savePesee} className="w-full bg-[#FF6B35] text-white hover:bg-[#c04008]">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Metrics Dialog (Tanita complet) */}
      <Dialog open={!!showMetrics} onOpenChange={() => setShowMetrics(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#FF00B8]" /> Metriques Tanita
            </DialogTitle>
          </DialogHeader>
          {metricsData && !metricsData.user_id && (
            <div className="bg-[#c0392b]/15 border border-[#c0392b]/30 rounded-md p-3 text-xs text-[#c0392b]">
              Aucun profil app lie. Creez d'abord le membre via le bouton "Creer membre".
            </div>
          )}
          <div className="space-y-3">
            <p className="text-[10px] text-[#AAAAAA] uppercase tracking-wider font-semibold">
              {metricsData?.metrics ? 'Derniere mesure enregistree — modifie pour ajouter une nouvelle entree' : 'Aucune mesure — remplis les champs'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[#AAAAAA] text-[10px]">Poids (kg)</Label><Input type="number" step="0.1" value={metricsForm.weight} onChange={e => setMetricsForm({...metricsForm, weight: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" data-testid="metrics-weight" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">Gras (%)</Label><Input type="number" step="0.1" value={metricsForm.body_fat} onChange={e => setMetricsForm({...metricsForm, body_fat: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" data-testid="metrics-fat" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">Muscle (kg)</Label><Input type="number" step="0.1" value={metricsForm.muscle_mass} onChange={e => setMetricsForm({...metricsForm, muscle_mass: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" data-testid="metrics-muscle" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">Os (kg)</Label><Input type="number" step="0.1" value={metricsForm.bone_mass} onChange={e => setMetricsForm({...metricsForm, bone_mass: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">Eau (%)</Label><Input type="number" step="0.1" value={metricsForm.body_water} onChange={e => setMetricsForm({...metricsForm, body_water: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">Graisse visc.</Label><Input type="number" step="1" value={metricsForm.visceral_fat} onChange={e => setMetricsForm({...metricsForm, visceral_fat: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">BMR (kcal)</Label><Input type="number" step="1" value={metricsForm.bmr} onChange={e => setMetricsForm({...metricsForm, bmr: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
              <div><Label className="text-[#AAAAAA] text-[10px]">Age metab.</Label><Input type="number" step="1" value={metricsForm.metabolic_age} onChange={e => setMetricsForm({...metricsForm, metabolic_age: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
            </div>
            <Button onClick={saveMetrics} disabled={savingMetrics}
              className="w-full bg-[#FF00B8] text-white hover:bg-[#CC0094] disabled:opacity-40" data-testid="save-metrics-btn">
              {savingMetrics ? 'Enregistrement...' : 'Sauvegarder les metriques'}
            </Button>
            <p className="text-[10px] text-[#AAAAAA] text-center">Les metriques sont enregistrees dans le profil app du membre et visibles sur son Dashboard.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* J0 + Objectif Dialog */}
      <Dialog open={!!editJ0} onOpenChange={() => setEditJ0(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#2A2A2E] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Mesures J0 & Objectif</DialogTitle></DialogHeader>
          <J0ObjectifForm j0Form={j0Form} setJ0Form={setJ0Form} onSave={saveJ0} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function J0ObjectifForm({ j0Form, setJ0Form, onSave }) {
  const poids = parseFloat(j0Form.poids_j0) || 0;
  const mm = parseFloat(j0Form.mm_j0) || 0;
  const pct = parseFloat(j0Form.pct_objectif) || 7;
  const obj = j0Form.objectif;
  const cibleP = poids > 0 ? (poids * (1 - pct / 100)).toFixed(1) : null;
  const cibleM = mm > 0 ? (mm * (1 + pct / 100)).toFixed(1) : null;
  const totalPerdre = poids > 0 ? (poids - parseFloat(cibleP)).toFixed(1) : 0;
  const totalGagner = mm > 0 ? (parseFloat(cibleM) - mm).toFixed(1) : 0;

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-[#FF6B35] font-semibold uppercase tracking-wider mb-2">Mesures J0</p>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-[#AAAAAA] text-[10px]">Poids (kg)</Label><Input type="number" step="0.1" value={j0Form.poids_j0} onChange={e => setJ0Form({...j0Form, poids_j0: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
          <div><Label className="text-[#AAAAAA] text-[10px]">Gras (%)</Label><Input type="number" step="0.1" value={j0Form.mg_j0} onChange={e => setJ0Form({...j0Form, mg_j0: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
          <div><Label className="text-[#AAAAAA] text-[10px]">Muscle (kg)</Label><Input type="number" step="0.1" value={j0Form.mm_j0} onChange={e => setJ0Form({...j0Form, mm_j0: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono" /></div>
        </div>
      </div>
      <div>
        <p className="text-xs text-[#FF6B35] font-semibold uppercase tracking-wider mb-2">Objectif principal</p>
        <div className="grid grid-cols-2 gap-2">
          {['gras', 'muscle'].map(o => (
            <button key={o} onClick={() => setJ0Form({...j0Form, objectif: o, pct_objectif: '4'})}
              className={`py-2.5 rounded-md text-sm font-semibold transition-colors ${j0Form.objectif === o ? 'bg-[#FF6B35] text-white' : 'bg-[#1B1B1B] text-[#AAAAAA] border border-[#2A2A2E]'}`}>
              {o === 'gras' ? 'Perte de gras' : 'Prise de muscle'}
            </button>
          ))}
        </div>
      </div>
      {poids > 0 && (
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-md p-3">
          <p className="text-[10px] text-zinc-500">Regle unique : poids −4% OU gras −4% OU muscle +4% (au prorata des paliers S4/S8/S12)</p>
        </div>
      )}
      <div>
        <Label className="text-[#AAAAAA] text-[10px]">% personnalise</Label>
        <div className="flex gap-2 items-center">
          <Input type="number" step="0.5" min="1" max="20" value={j0Form.pct_objectif} onChange={e => setJ0Form({...j0Form, pct_objectif: e.target.value})} className="bg-[#1B1B1B] border-[#2A2A2E] text-white font-mono flex-1" data-testid="custom-pct-input" />
          <span className="text-sm text-[#AAAAAA]">%</span>
        </div>
        <input type="range" min="1" max="15" step="0.5" value={j0Form.pct_objectif} onChange={e => setJ0Form({...j0Form, pct_objectif: e.target.value})} className="w-full mt-2 accent-[#FF6B35]" />
        <div className="flex justify-between text-[10px] text-[#AAAAAA]"><span>1%</span><span>15%</span></div>
      </div>
      {poids > 0 && mm > 0 && (
        <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-md p-3" data-testid="objectif-preview">
          <p className="text-xs text-[#FF6B35] font-bold mb-2">
            {obj === 'gras' ? `Objectif : ${poids}kg → ${cibleP}kg (perdre ${totalPerdre}kg)` : `Objectif : ${mm}kg muscle → ${cibleM}kg (gagner ${totalGagner}kg)`}
          </p>
          <div className="grid grid-cols-3 gap-1.5 text-center">
            {[{l:'S4',c:0.33},{l:'S8',c:0.66},{l:'S12',c:1.0}].map(({l, c}) => {
              const pc = obj === 'gras' ? (poids - parseFloat(totalPerdre) * c).toFixed(1) : null;
              const mc = obj === 'muscle' ? (mm + parseFloat(totalGagner) * c).toFixed(1) : null;
              return (<div key={l} className="bg-[#0F0F0F] rounded p-1.5"><p className="text-[10px] text-[#AAAAAA]">{l}</p><p className="font-mono text-xs text-white">{obj === 'gras' ? `${pc}kg` : `${mc}kg`}</p></div>);
            })}
          </div>
        </div>
      )}
      <Button onClick={onSave} className="w-full bg-[#FF6B35] text-white hover:bg-[#FF8C55]">Sauvegarder l'objectif</Button>
    </div>
  );
}
